import React, { useEffect, useRef, useState, useCallback } from 'react';
import { updateIDMapping, extractTemplateField, getUpdatedHTML } from './inspectorHelper';

const HtmlViewerWithInspector = ({
    htmlCode,
    cssCode,
    experience_json,
    configJsonData = [],
    configFieldNameMain,
    inspectorEnabled,
    onElementUpdate,
    web_url,
    templateType
}) => {
    const iframeRef = useRef(null);

    // No React-layer hoverBox/tooltip — they live inside the iframe DOM exactly like preview.html

    // Modal States
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedElementInfo, setSelectedElementInfo] = useState({ tag: '', id: '', path: [] });
    const [idSelectValue, setIdSelectValue] = useState('');
    const [contentSelectValue, setContentSelectValue] = useState('');
    const [showContentSection, setShowContentSection] = useState(false);
    const [contentSectionTitle, setContentSectionTitle] = useState('Element Content');
    const [elementClasses, setElementClasses] = useState([]);
    const [globalStyles, setGlobalStyles] = useState({});

    // Refs to keep stable references inside iframe callbacks
    const rawHtmlRef = useRef('');
    const inspectorEnabledRef = useRef(inspectorEnabled);
    const configJsonDataRef = useRef(configJsonData);
    const cssCodeRef = useRef(cssCode);
    // Store selected element info in a ref for use inside saveChanges
    const selectedElementRef = useRef(null);
    const modalStateRef = useRef({
        idSelectValue: '',
        contentSelectValue: '',
        elementClasses: [],
        globalStyles: {},
        selectedElementInfo: { tag: '', id: '', path: [] },
        showContentSection: false,
        contentSectionTitle: 'Element Content',
    });

    useEffect(() => { inspectorEnabledRef.current = inspectorEnabled; }, [inspectorEnabled]);
    useEffect(() => { configJsonDataRef.current = configJsonData; }, [configJsonData]);
    useEffect(() => { cssCodeRef.current = cssCode; }, [cssCode]);

    // ---- Helpers ----
    const getPathFromTemplate = (node, root) => {
        const path = [];
        let current = node;
        while (current && current !== root && current.nodeType === 1) {
            if (!current.parentElement) break;
            const siblings = Array.from(current.parentElement.children).filter(
                el => el.tagName !== 'STYLE' && el.tagName !== 'SCRIPT'
            );
            const idx = siblings.indexOf(current);
            if (idx < 0) break;
            path.push(idx);
            current = current.parentElement;
        }
        return path.reverse();
    };

    const findNodeByPath = (containerRoot, path) => {
        let cur = containerRoot;
        for (let i = 0; i < path.length; i++) {
            if (!cur) return null;
            const children = Array.from(cur.children).filter(
                el => el.tagName !== 'STYLE' && el.tagName !== 'SCRIPT'
            );
            if (children.length <= path[i]) return null;
            cur = children[path[i]];
        }
        return cur || null;
    };

    const extractStyleRulesFromString = (cssString) => {
        const rules = {};
        const regex = /\.([a-zA-Z0-9_-]+)\s*\{\s*([^}]+)\}/g;
        let match;
        while ((match = regex.exec(cssString)) !== null) {
            rules[match[1]] = match[2].trim();
        }
        return rules;
    };

    // Extract style rules from the iframe's live style tags (mirrors preview.html extractStyleRules)
    const extractStyleRulesFromIframe = () => {
        const doc = iframeRef.current?.contentDocument;
        if (!doc) return {};
        const rules = {};
        doc.querySelectorAll('#template style').forEach(style => {
            const regex = /\.([a-zA-Z0-9_-]+)\s*\{\s*([^}]+)\}/g;
            let match;
            while ((match = regex.exec(style.textContent)) !== null) {
                rules[match[1]] = match[2].trim();
            }
        });
        return rules;
    };

    // Helper: get the highlight box div from inside the iframe
    const getIframeHighlightBox = () => iframeRef.current?.contentDocument?.querySelector('.strd-highlight');
    const getIframeTooltip = () => iframeRef.current?.contentDocument?.getElementById('inspector-tooltip');

    // ---- Iframe event handlers (stable, defined once, attached via useEffect) ----
    const handleIframeMouseover = useCallback((e) => {
        const doc = iframeRef.current?.contentDocument;
        if (!doc) return;

        const highlightBox = getIframeHighlightBox();
        const tooltipEl = getIframeTooltip();
        const dztTarget = e.target.closest('[dzt-id]');

        if (inspectorEnabledRef.current) {
            // Inspector mode: add inspector-highlight CSS class to hovered element + show tooltip
            const element = e.target;

            if (iframeRef.current.__prevHovered && iframeRef.current.__prevHovered !== element) {
                iframeRef.current.__prevHovered.classList.remove('inspector-highlight');
            }
            iframeRef.current.__prevHovered = element;
            element.classList.add('inspector-highlight');

            if (tooltipEl) {
                const tagName = element.tagName.toLowerCase();
                const id = element.id ? `#${element.id}` : '';
                const classes = element.className ? `.${element.className.split(' ').join('.')}` : '';
                tooltipEl.textContent = `${tagName}${id}${classes}`;
                tooltipEl.style.left = e.clientX + 10 + 'px';
                tooltipEl.style.top = e.clientY + 10 + 'px';
                tooltipEl.style.display = 'block';
            }

            // Also position the strd-highlight box for dzt-id elements
            if (highlightBox && dztTarget) {
                const rect = dztTarget.getBoundingClientRect();
                highlightBox.style.width = rect.width + 'px';
                highlightBox.style.height = rect.height + 'px';
                highlightBox.style.left = rect.left + 'px';
                highlightBox.style.top = rect.top + 'px';
                highlightBox.style.display = 'block';
            } else if (highlightBox) {
                highlightBox.style.display = 'none';
            }
        } else {
            // Non-inspector mode: show strd-highlight box only for dzt-id elements (preview.html lines 1752-1764)
            const directDztTarget = e.target.getAttribute('dzt-id') ? e.target : dztTarget;
            if (highlightBox && directDztTarget && e.target.tagName.toLowerCase() !== 'body') {
                const rect = directDztTarget.getBoundingClientRect();
                highlightBox.style.width = rect.width + 'px';
                highlightBox.style.height = rect.height + 'px';
                highlightBox.style.left = rect.left + 'px';
                highlightBox.style.top = rect.top + 'px';
                highlightBox.style.display = 'block';
            } else if (highlightBox) {
                highlightBox.style.display = 'none';
            }
        }
    }, []);

    const handleIframeMouseout = useCallback((e) => {
        const highlightBox = getIframeHighlightBox();
        const tooltipEl = getIframeTooltip();
        if (inspectorEnabledRef.current) {
            if (iframeRef.current?.__prevHovered) {
                iframeRef.current.__prevHovered.classList.remove('inspector-highlight');
                iframeRef.current.__prevHovered = null;
            }
            if (tooltipEl) tooltipEl.style.display = 'none';
        }
        if (highlightBox) highlightBox.style.display = 'none';
    }, []);

    const handleIframeClick = useCallback((e) => {
        if (inspectorEnabledRef.current) {
            // Inspector mode: open the edit modal (mirrors preview.html handleClick)
            e.preventDefault();
            e.stopPropagation();

            const doc = iframeRef.current?.contentDocument;
            if (!doc) return;

            const target = e.target;
            const tagName = target.tagName.toLowerCase();
            const path = getPathFromTemplate(target, doc.querySelector('#template'));

            // Find raw node
            let rawNode = null;
            if (rawHtmlRef.current) {
                const tempDiv = document.createElement('div');
                tempDiv.innerHTML = rawHtmlRef.current;
                const dztId = target.getAttribute('dzt-id');
                if (dztId) {
                    try { rawNode = tempDiv.querySelector(`[dzt-id="${dztId}"]`); } catch (_) {}
                }
                if (!rawNode && path.length > 0) rawNode = findNodeByPath(tempDiv, path);
                if (!rawNode && target.id) {
                    try { rawNode = tempDiv.querySelector(`[id="${target.id}"]`); } catch (_) {}
                }
            }

            const dztId = rawNode ? rawNode.getAttribute('dzt-id') : target.getAttribute('dzt-id');

            // Content extraction
            let contentField = null;
            let showContent = false;
            let contentTitle = 'Element Content';

            if (tagName === 'img') {
                const srcValue = rawNode ? (rawNode.getAttribute('src') || '') : (target.getAttribute('src') || '');
                contentField = extractTemplateField(srcValue);
                showContent = true;
                contentTitle = 'Image Source';
            } else if (target.children.length === 0 && !['input', 'br', 'hr'].includes(tagName)) {
                const contentValue = rawNode
                    ? (rawNode.innerHTML || rawNode.textContent || '')
                    : (target.innerHTML || target.textContent || '');
                contentField = extractTemplateField(contentValue);
                showContent = true;
                contentTitle = 'Element Content';
            }

            // Extract CSS rules from iframe style tags
            const styles = extractStyleRulesFromIframe();
            const classes = (target.className || '').split(/\s+/).filter(c => c && !c.includes('inspector'));
            const mappedClasses = classes.map(cls => ({ name: cls, css: styles[cls] || '', isEditing: false }));

            // Check if selected option exists in configJsonData
            const currentData = configJsonDataRef.current;
            const idExists = currentData.some(item => item.exp_json_field === dztId);
            const idVal = idExists ? (dztId || '') : '';
            const contentExists = currentData.some(item => item.exp_json_field === contentField);
            const contentVal = contentExists ? (contentField || '') : '';

            // Update state AND refs (so saveChanges inside modal has latest values)
            const newInfo = { tag: tagName, id: target.getAttribute('dzt-id') || target.id || 'none', path };
            modalStateRef.current = {
                idSelectValue: idVal,
                contentSelectValue: contentVal,
                selectedElementInfo: newInfo,
                showContentSection: showContent,
                contentSectionTitle: contentTitle,
                elementClasses: mappedClasses,
                globalStyles: styles,
            };

            selectedElementRef.current = target;
            selectedElementRef.current.__strd_path = path;

            setSelectedElementInfo(newInfo);
            setIdSelectValue(idVal);
            setContentSelectValue(contentVal);
            setShowContentSection(showContent);
            setContentSectionTitle(contentTitle);
            setElementClasses(mappedClasses);
            setGlobalStyles(styles);
            setIsModalOpen(true);
            // Hide the in-iframe highlight and tooltip when modal opens
            const hb = getIframeHighlightBox();
            const tt = getIframeTooltip();
            if (hb) hb.style.display = 'none';
            if (tt) tt.style.display = 'none';

        } else {
            // Non-inspector mode: clicking a dzt-id element posts the id to the parent window
            // exactly like preview.html's sendMessageToParent(dztId) — the accordion's
            // window.addEventListener('message', iframeHandler) already handles this string message
            // to scroll to and focus the matching config form field.
            if (
                e.target.tagName.toLowerCase() !== 'body' &&
                e.target !== iframeRef.current?.contentDocument?.querySelector('.strd-highlight')
            ) {
                const dztId = e.target.getAttribute('dzt-id');
                if (dztId) {
                    e.preventDefault();
                    // Post raw string — matches accordion's: if (el.id === event.data) scrollToField(idx)
                    window.postMessage(dztId, window.location.origin);
                }
            }
        }
    }, [onElementUpdate]);

    // ---- Main render effect ----
    useEffect(() => {
        if (!iframeRef.current) return;
        const iframe = iframeRef.current;
        const doc = iframe.contentDocument;
        if (!doc) return;

        // Extract the CSP nonce injected by nginx into the parent page
        let pageNonce = '';
        const scriptWithNonce = document.querySelector('script[nonce]');
        if (scriptWithNonce) {
            pageNonce = scriptWithNonce.nonce || scriptWithNonce.getAttribute('nonce') || '';
        }

        let htmlContent = getUpdatedHTML(experience_json, htmlCode);
        if (pageNonce) {
            // Inject nonce into all <script> tags in the template HTML so they aren't blocked by CSP
            htmlContent = htmlContent.replace(/<script\b(?![^>]*\bnonce=)[^>]*>/gi, (match) => {
                return match.replace('<script', `<script nonce="${pageNonce}"`);
            });
        }
        const bgStyle = web_url
            ? `background-image:url('${web_url}');background-size:cover;background-position:center;background-repeat:no-repeat;`
            : '';

        const fullHtml = `<!DOCTYPE html>
<html class="w-full h-full">
<head>
<meta charset="UTF-8"/>
<meta name="viewport" content="width=device-width, initial-scale=1.0"/>
<style>
  .strd-highlight {
    position: fixed;
    background-color: rgba(173, 216, 230, 0.5);
    border: 2px solid rgba(0, 0, 255, 0.5);
    z-index: 2147483647 !important;
    pointer-events: none;
    border-radius: 2px;
    box-sizing: content-box;
    margin: 0 !important;
    padding: 0 !important;
    display: none;
  }
  .inspector-highlight {
    outline: 2px solid #007bff !important;
    outline-offset: 2px;
    background: rgba(0, 123, 255, 0.1) !important;
    cursor: pointer !important;
  }
  #inspector-tooltip {
    position: fixed;
    background: #333;
    color: white;
    padding: 5px 10px;
    border-radius: 3px;
    font-size: 12px;
    pointer-events: none;
    z-index: 10001;
    display: none;
    font-family: sans-serif;
  }
  html, body { margin: 0; padding: 0; height: 100%; width: 100%; }
  * { box-sizing: border-box; }
  ${cssCode}
</style>
</head>
<body class="w-full h-full" style="${bgStyle}">
  <div id="template" class="w-full h-full">${htmlContent}</div>
  <div class="strd-highlight"></div>
  <div id="inspector-tooltip"></div>
  <script nonce="${pageNonce}">
    setTimeout(function() {
      if ("${templateType}" === "json_a_to_html" && typeof window.initializeJSONtoHTML === 'function') {
        window.initializeJSONtoHTML(${JSON.stringify(configJsonData)});
      } else if (typeof window.initializeTemplate === 'function') {
        window.initializeTemplate();
      }
    }, 100);
  </script>
</body>
</html>`;

        doc.open();

        const iframeWin = iframeRef.current.contentWindow;
        let safeHtml = fullHtml;

        if (iframeWin && iframeWin.trustedTypes && iframeWin.trustedTypes.createPolicy) {
            try {
                // Reuse if already created on this exact window instance
                if (!iframeRef.current.__ttPolicy) {
                    iframeRef.current.__ttPolicy = iframeWin.trustedTypes.createPolicy('default', {
                        createHTML: (input) => input,
                        createScript: (input) => input,
                        createScriptURL: (input) => input,
                    });
                }
                safeHtml = iframeRef.current.__ttPolicy.createHTML(fullHtml);
            } catch (e) {
                // Don't silently swallow this — log it so it's visible if it fails again
                console.error('[TrustedTypes] iframe policy failed, HTML will be blocked:', e);
            }
        }

        doc.write(safeHtml);
        doc.close();

        rawHtmlRef.current = htmlCode;

        // Attach listeners to the iframe document
        doc.addEventListener('mouseover', handleIframeMouseover);
        doc.addEventListener('mouseout', handleIframeMouseout);
        doc.addEventListener('click', handleIframeClick, true);

        return () => {
            try {
                doc.removeEventListener('mouseover', handleIframeMouseover);
                doc.removeEventListener('mouseout', handleIframeMouseout);
                doc.removeEventListener('click', handleIframeClick, true);
            } catch (_) {}
        };
    }, [htmlCode, cssCode, experience_json, web_url, templateType, configJsonData, handleIframeMouseover, handleIframeMouseout, handleIframeClick]);

    // Clean up inspector artifacts when inspector is toggled off
    useEffect(() => {
        if (!inspectorEnabled) {
            const doc = iframeRef.current?.contentDocument;
            if (doc) {
                if (iframeRef.current.__prevHovered) {
                    iframeRef.current.__prevHovered.classList.remove('inspector-highlight');
                    iframeRef.current.__prevHovered = null;
                }
                const tooltipEl = doc.getElementById('inspector-tooltip');
                if (tooltipEl) tooltipEl.style.display = 'none';
                const highlightBox = doc.querySelector('.strd-highlight');
                if (highlightBox) highlightBox.style.display = 'none';
            }
        }
    }, [inspectorEnabled]);

    // ---- Modal Save (mirrors preview.html submitBtn click handler) ----
    const handleModalSave = () => {
        const finalId = idSelectValue.trim();
        const finalContentField = contentSelectValue.trim();
        const finalContent = finalContentField ? `\${template.${finalContentField}}` : '';

        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = rawHtmlRef.current || '';

        // Find node by path, then by id as fallback
        let node = null;
        if (Array.isArray(selectedElementInfo.path)) {
            node = findNodeByPath(tempDiv, selectedElementInfo.path);
        }
        if (!node && selectedElementRef.current?.id) {
            try { node = tempDiv.querySelector(`[id="${selectedElementRef.current.id}"]`); } catch (_) {}
        }

        if (node) {
            if (finalId) {
                node.setAttribute('dzt-id', finalId);
                if (node.id) node.id = finalId;
            }
            if (finalContent) {
                const tag = node.tagName?.toLowerCase() || '';
                if (tag === 'img') {
                    node.setAttribute('src', finalContent);
                } else {
                    node.textContent = finalContent;
                }
            }
        }

        let updatedRawHtml = tempDiv.innerHTML;

        // Update CSS in raw HTML (mirrors preview.html submitBtn logic)
        const styleRegex = /<style[^>]*>([\s\S]*?)<\/style>/;
        const styleMatch = updatedRawHtml.match(styleRegex);
        if (styleMatch) {
            let styleContent = styleMatch[1];
            Object.keys(globalStyles).forEach(cls => {
                const cssRule = globalStyles[cls];
                const classRegex = new RegExp(`\\.${cls}\\s*\\{[^}]*\\}`, 'g');
                if (styleContent.match(classRegex)) {
                    styleContent = styleContent.replace(classRegex, `.${cls} { ${cssRule} }`);
                } else {
                    styleContent += `\n      .${cls} { ${cssRule} }`;
                }
            });
            updatedRawHtml = updatedRawHtml.replace(styleMatch[0], `<style>\n      ${styleContent}\n    </style>`);
        }

        // Rebuild updatedRawCss from globalStyles (for the editor pane)
        let updatedRawCss = cssCode;
        elementClasses.forEach(cls => {
            if (globalStyles[cls.name] !== cls.css) {
                const cssRule = cls.css;
                const classRegex = new RegExp(`\\.${cls.name}\\s*\\{[^}]*\\}`, 'g');
                if (updatedRawCss.match(classRegex)) {
                    updatedRawCss = updatedRawCss.replace(classRegex, `.${cls.name} { ${cssRule} }`);
                } else {
                    updatedRawCss += `\n.${cls.name} { ${cssRule} }`;
                }
            }
        });

        // Apply ID mapping update if ID changed
        let finalHtmlString = updatedRawHtml;
        if (finalId && selectedElementInfo.id !== finalId) {
            try {
                const result = updateIDMapping(updatedRawHtml, selectedElementInfo.id, finalId, configFieldNameMain);
                finalHtmlString = result.html;
            } catch (e) {
                console.error('ID Mapping update failed:', e);
            }
        }

        if (onElementUpdate) {
            onElementUpdate({
                type: 'element-updated',
                id: finalId,
                content: finalContent,
                rawHtml: finalHtmlString,
                rawCss: updatedRawCss,
            });
        }

        rawHtmlRef.current = finalHtmlString;
        setIsModalOpen(false);
        selectedElementRef.current = null;
    };

    const handleModalCancel = () => {
        setIsModalOpen(false);
        selectedElementRef.current = null;
        setIdSelectValue('');
        setContentSelectValue('');
    };

    return (
        <div style={{ position: 'relative', width: '100%', height: '100%' }}>
            <iframe
                ref={iframeRef}
                style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
                title="template-preview"
            />

            {/* Highlight box and tooltip are rendered inside the iframe DOM — no React overlays needed */}

            {/* Inspector Modal */}
            {isModalOpen && (
                <div
                    style={{
                        position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
                        background: 'rgba(0,0,0,0.7)', zIndex: 100001,
                        display: 'flex', justifyContent: 'center', alignItems: 'center',
                        fontFamily: 'sans-serif',
                    }}
                    onClick={(e) => { if (e.target === e.currentTarget) handleModalCancel(); }}
                >
                    <div style={{
                        background: '#2d2d30', color: '#d4d4d4', padding: 30,
                        borderRadius: 8, width: '90%', maxWidth: 650,
                        maxHeight: '85vh', overflowY: 'auto',
                        boxShadow: '0 5px 20px rgba(0,0,0,0.5)',
                    }}>
                        <h3 style={{ color: '#fff', marginTop: 0, marginBottom: 10, fontSize: 18 }}>✏️ Edit Element</h3>
                        <p style={{ fontSize: 14, color: '#ccc', marginBottom: 20 }}>
                            Element: <strong>&lt;{selectedElementInfo.tag}&gt; (current ID: {selectedElementInfo.id})</strong>
                        </p>

                        {/* ID Section */}
                        <div style={{ marginBottom: 25, paddingBottom: 20, borderBottom: '1px solid #444' }}>
                            <h4 style={{ fontSize: 13, color: '#4ec9b0', marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Element ID</h4>
                            <select
                                value={idSelectValue}
                                onChange={e => setIdSelectValue(e.target.value)}
                                style={{ width: '100%', padding: 10, background: '#3c3c3c', color: '#d4d4d4', border: '1px solid #555', borderRadius: 4, fontSize: 14 }}
                            >
                                <option value="">-- Select ID Field --</option>
                                {configJsonData.map((item, idx) => (
                                    <option key={idx} value={item.exp_json_field}>{item.exp_json_field}</option>
                                ))}
                            </select>
                        </div>

                        {/* Content Section */}
                        {showContentSection && (
                            <div style={{ marginBottom: 25, paddingBottom: 20, borderBottom: '1px solid #444' }}>
                                <h4 style={{ fontSize: 13, color: '#4ec9b0', marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{contentSectionTitle}</h4>
                                <select
                                    value={contentSelectValue}
                                    onChange={e => setContentSelectValue(e.target.value)}
                                    style={{ width: '100%', padding: 10, background: '#3c3c3c', color: '#d4d4d4', border: '1px solid #555', borderRadius: 4, fontSize: 14 }}
                                >
                                    <option value="">-- Select Content Field --</option>
                                    {configJsonData.map((item, idx) => (
                                        <option key={idx} value={item.exp_json_field}>{item.exp_json_field}</option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {/* CSS Classes Section */}
                        <div style={{ marginBottom: 25 }}>
                            <h4 style={{ fontSize: 13, color: '#4ec9b0', marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.5px' }}>CSS Classes &amp; Styles</h4>
                            {elementClasses.length === 0 ? (
                                <div style={{ color: '#999', fontSize: 12 }}>No classes assigned to this element</div>
                            ) : (
                                elementClasses.map((cls, idx) => (
                                    <div key={idx} style={{ background: '#3c3c3c', padding: 12, borderRadius: 4, marginBottom: 12, border: '1px solid #444' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                            <span style={{ fontWeight: 600, color: '#4ec9b0', fontSize: 13 }}>.{cls.name}</span>
                                            <button
                                                onClick={() => {
                                                    const updated = elementClasses.map((c, i) => i === idx ? { ...c, isEditing: !c.isEditing } : c);
                                                    setElementClasses(updated);
                                                }}
                                                style={{ padding: '4px 10px', background: cls.isEditing ? '#16825d' : '#555', color: 'white', border: 'none', borderRadius: 3, cursor: 'pointer', fontSize: 11 }}
                                            >
                                                {cls.isEditing ? 'Done' : 'Edit CSS'}
                                            </button>
                                        </div>
                                        {cls.isEditing && (
                                            <textarea
                                                rows={6}
                                                value={cls.css}
                                                onChange={e => {
                                                    const updated = elementClasses.map((c, i) => i === idx ? { ...c, css: e.target.value } : c);
                                                    setElementClasses(updated);
                                                    setGlobalStyles(prev => ({ ...prev, [cls.name]: e.target.value }));
                                                }}
                                                style={{ width: '100%', padding: 8, background: '#1e1e1e', border: '1px solid #007acc', borderRadius: 3, color: '#d4d4d4', fontFamily: 'monospace', fontSize: 12, minHeight: 70, boxSizing: 'border-box' }}
                                            />
                                        )}
                                        {!cls.isEditing && cls.css && (
                                            <div style={{ fontSize: 12, color: '#d4d4d4', wordBreak: 'break-all', lineHeight: 1.4 }}>{cls.css}</div>
                                        )}
                                    </div>
                                ))
                            )}
                        </div>

                        {/* Buttons */}
                        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', borderTop: '1px solid #444', paddingTop: 15 }}>
                            <button onClick={handleModalCancel} style={{ padding: '8px 16px', background: '#555', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 13 }}>Cancel</button>
                            <button onClick={handleModalSave} style={{ padding: '8px 16px', background: '#0e639c', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 13 }}>💾 Save &amp; Update</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default HtmlViewerWithInspector;
